<script setup lang="ts">
import {
  useListVisits,
  TimelineActionType,
  type ExportFilterPayload,
  useCountVisits,
  useGetVisitFields,
  type VisitWithFields,
} from "@/api/visits";
import {
  ReviewStatus,
  type ListVisitFieldsItemPagedApiResponse,
} from "@/api/zoolander";
import CounterPill from "@/components/common/CounterPill/CounterPill.vue";
import PageTitle from "@/components/common/PageTitle/PageTitle.vue";
import AddVisitorTimelineDrawer from "./components/AddVisitorTimelineDrawer.vue";
import VisitorTimelineFilters from "@/pages/visitors/components/VisitorTimelineFilters.vue";
import TableFilters from "@/components/common/TableFilters/TableFilters.vue";
import EditVisitorTimelineDrawer from "./components/EditVisitorTimelineDrawer.vue";
import DrawerHeaderIcons from "@/components/common/DrawerHeaderIcons/DrawerHeaderIcons.vue";
import AnonymizeVisitDialog from "./components/AnonymizeVisitDialog.vue";
import SignOutVisitDialog from "./components/SignOutVisitDialog.vue";
import { computed, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { STATUS, SOCKET_EVENT } from "@/stores/constants";
import { useDeepLinking } from "@/composables/deepLinking";
import { usePageDrawer } from "@/composables/drawer";
import type { Status } from "@/api/employeeMovements";
import { useVisits } from "@/api/visits/visits";
import { useLiveUpdates } from "@/composables/liveUpdates";
import VisitorTimelineTable from "./components/VisitorTimelineTable.vue";
import dayjs from "dayjs";
import { type TableAction } from "./types";
import useRouteQueryParams from "@/composables/routeQueryParams";
import { Shortcut, type DateRangeFilter } from "@/composables/dateShortcuts";
import { useAuth } from "@/composables/auth";
import type { ColumnSelectionItem } from "@/components/common/ColumnSelector/ColumnSelector.vue";
import { useGetCompany } from "@/api/companies";

const { t } = useI18n();

const {
  setQueryParam,
  setDateQueryParam,
  getQueryParam,
  queryParamToDateFilter,
  DateRangeFilterToDateTuple,
} = useRouteQueryParams();

const defaultSortField = "startedAt";
const defaultSortOrder = "desc";

const tableRef = ref();
const currentPage = ref(1);
const numberOfPages = ref(1);
const sort = ref(getQueryParam("sort", "startedAt:desc"));
const statusFilter = ref(getQueryParam("status", STATUS.ALL));
const categoryFilter = ref(getQueryParam("category", STATUS.ALL));
const searchFilter = ref(getQueryParam("search", ""));
const dateRangeFilter = ref<DateRangeFilter>(queryParamToDateFilter());
const dateFilter = ref();
const signOutDialogVisible = ref(false);
const anonymizeDialogVisible = ref(false);
const selectedRows = ref<VisitWithFields[]>([]);
const selectedVisit = ref<VisitWithFields>();

const { isCompanyAdmin, isSuperAdmin } = useAuth();
const { data: companyData } = useGetCompany();

const hasFilters = computed(
  () => statusFilter.value !== STATUS.ALL || categoryFilter.value !== STATUS.ALL
);

const statusQueryFilter = computed(() => {
  switch (statusFilter.value) {
    case STATUS.IN:
      return {
        complete: false,
        reviewStatus: "allow,deny,lapsed",
      };
    case STATUS.OUT:
      return {
        complete: true,
        reviewStatus: "allow,deny,lapsed",
      };
    case STATUS.PENDING:
      return {
        reviewStatus: "pending",
      };
    case STATUS.DENIED:
      return {
        reviewStatus: "deny",
      };
    default:
      return {};
  }
});

const categoryQueryFilter = computed(() =>
  categoryFilter.value === STATUS.ALL
    ? {}
    : {
        categoryName: categoryFilter.value,
      }
);

const dateRangeQueryFilter = computed(() => {
  const { dateRange, shortcut } = dateRangeFilter.value;
  const startDate = dayjs(dateRange[0]);
  const endDate = dayjs(dateRange[1]);
  const startDateFormatted = startDate.format("YYYY-MM-DD");
  const endDateFormatted = endDate.format("YYYY-MM-DD");

  if (
    [Shortcut.TODAY, Shortcut.LAST_7_DAYS, Shortcut.LAST_30_DAYS].includes(
      shortcut
    )
  ) {
    return {
      export: {
        startDate: startDateFormatted,
      },
      action: {
        visit_movement: `>=${startDateFormatted}`,
      },
    };
  }

  return {
    export: {
      startDate: startDateFormatted,
      endDate: endDateFormatted,
    },
    action: {
      visit_movement: `>=${startDateFormatted} 00:00:00,<=${endDateFormatted} 23:59:59`,
    },
  };
});

watch(
  dateRangeFilter,
  (newDateRange) => {
    dateFilter.value = DateRangeFilterToDateTuple(newDateRange);
  },
  { immediate: true }
);

const startDateFilter = computed(() => dateFilter.value[0]);
const endDateFilter = computed(() => dateFilter.value[1]);

const filter = computed(() => ({
  startedAt: startDateFilter.value,
  finishedAt: endDateFilter.value,
  ...statusQueryFilter.value,
  ...categoryQueryFilter.value,
  q: searchFilter.value,
  page: currentPage.value,
  sort,
}));

const countFilter = computed(() => ({
  startedAt: dayjs(companyData.value?.subscription.joinedAt)
    .startOf("day")
    .toDate(),
  finishedAt: dayjs().endOf("day").toDate(),
}));

const {
  signOutVisitor,
  approveVisitor,
  rejectVisitor,
  exportVisits,
  signOutAllVisits,
  signOutSelectedVisits,
  anonymizeVisits,
  isExportLoading,
  isSignOutAllLoading,
  isSignOutSelectedLoading,
  isAnonymizeLoading,
} = useVisits(filter);

const {
  data: visitorTimelineData,
  isLoading,
  isFetching,
  isPreviousData,
  refetch,
} = useListVisits(filter, (data) => {
  numberOfPages.value = data.pagination?.pages ?? 1;
});

const { data: filteredCountsData, refetch: refetchCounts } =
  useCountVisits(filter);
const { data: countsData, refetch: refetchFilteredCounts } =
  useCountVisits(countFilter);

const visitIds = computed(() =>
  visitorTimelineData.value?.data.length
    ? visitorTimelineData.value.data
        .filter((visit) => !visit.anonymizedAt)
        .map((visit) => visit.id)
    : []
);

const loadFields = computed(
  () =>
    tableRef.value?.columns?.[1].items.some(
      (item: ColumnSelectionItem) => item.value
    )
);
const fieldsData = useGetVisitFields(visitIds, loadFields);

const visitsWithFields = computed(() => {
  const mappedFieldsData = fieldsData
    .filter((result) => result.data !== undefined)
    .map(
      (result) =>
        result.data as ListVisitFieldsItemPagedApiResponse & {
          visitId: string;
        }
    );

  return visitorTimelineData.value?.data.map((visit) => {
    const fields = mappedFieldsData.find((data) => data.visitId === visit.id);

    return {
      ...visit,
      fields: fields?.data ?? [],
    };
  });
});

useLiveUpdates({
  [SOCKET_EVENT.VISIT_NEW]: {
    fn: () => {
      refetch();
      refetchCounts();
      refetchFilteredCounts();
    },
  },
  [SOCKET_EVENT.VISIT_EDIT]: {
    fn: () => {
      refetch();
      refetchCounts();
      refetchFilteredCounts();
    },
  },
  [SOCKET_EVENT.VISIT_CLOSE]: {
    fn: () => {
      refetch();
      refetchCounts();
      refetchFilteredCounts();
    },
  },
});

const {
  selectedId,
  editDrawerVisible,
  openEditDrawer,
  closeEditDrawer,
  addDrawerVisible,
  openAddDrawer,
  closeAddDrawer,
} = useDeepLinking("visitors-timeline");

const { prevDisabled, nextDisabled, goPrev, goNext } = usePageDrawer(
  computed(() => visitsWithFields.value ?? []),
  selectedId,
  (visitorTimeline) => handleOpenEditDrawer(visitorTimeline),
  (visitorTimeline) => handleOpenEditDrawer(visitorTimeline)
);

const counts = computed(() => filteredCountsData.value?.data);
const totalIn = computed(() => countsData.value?.data.in ?? 0);

const isSignOutLoading = computed(
  () => isSignOutSelectedLoading.value || isSignOutAllLoading.value
);

const exportActionCount = computed(() => {
  return (
    selectedRows.value.length ||
    visitorTimelineData.value?.pagination.items ||
    0
  );
});

const signoutable = computed(() =>
  selectedRows.value.filter(
    (visit: VisitWithFields) =>
      visit.startedAt &&
      !visit.finishedAt &&
      visitStatus(visit) !== STATUS.PENDING
  )
);

const signOutActionCount = computed(
  () =>
    signoutable.value.length ||
    (!selectedRows.value.length && counts?.value?.in) ||
    0
);

const anonymizable = computed(() =>
  selectedRows.value.filter(
    ({ anonymizedAt, startedAt, finishedAt }) =>
      (!anonymizedAt && startedAt && finishedAt) ||
      (!anonymizedAt && !startedAt && !finishedAt)
  )
);

const anonymizeActionCount = computed(() => anonymizable.value.length);

const actionExportFilters = computed(() => {
  const filters: {
    action: { [key: string]: string };
    export: ExportFilterPayload;
  } = { action: {}, export: { review_status: "allow,deny,pending,lapsed" } };

  if (searchFilter.value) {
    filters.action.full_name = searchFilter.value.trim();
    filters.action.company_name = searchFilter.value.trim();
    filters.export.search = searchFilter.value.trim();
  }

  switch (statusFilter.value) {
    case STATUS.IN:
      filters.action.visit_fact_to = "null";
      filters.export.status = STATUS.IN;
      break;
    case STATUS.OUT:
      filters.action.visit_fact_to = "!null";
      filters.export.status = STATUS.OUT;
      break;
    case STATUS.PENDING:
      filters.action.review_status = STATUS.PENDING;
      filters.export.review_status = STATUS.PENDING;
      break;
    case STATUS.DENIED:
      filters.action.review_status = STATUS.DENY;
      filters.export.review_status = STATUS.DENY;
      break;
    default:
      break;
  }
  if (
    categoryFilter.value !== STATUS.ALL &&
    categoryQueryFilter.value["categoryName"]
  ) {
    filters.export.category_name = categoryQueryFilter.value["categoryName"];
    filters.action.category_name = categoryQueryFilter.value["categoryName"];
  }

  Object.assign(filters.action, dateRangeQueryFilter.value?.action);
  Object.assign(filters.export, dateRangeQueryFilter.value?.export);

  return filters;
});

const hasAdminRights = computed(
  () => isCompanyAdmin.value || isSuperAdmin.value
);

const visitStatus = (visit: VisitWithFields) => {
  if (visit.anonymizedAt) {
    return STATUS.ANONYMIZED;
  }
  if (visit.reviewStatus === ReviewStatus.Pending) {
    return STATUS.PENDING;
  }
  if (
    visit.reviewStatus &&
    [ReviewStatus.Deny, ReviewStatus.Lapsed].includes(visit.reviewStatus)
  ) {
    return STATUS.DENIED;
  }
  if (visit.startedAt && !visit.finishedAt) {
    return STATUS.IN;
  }
  return STATUS.OUT;
};

const handlePageChange = (page: number) => {
  currentPage.value = page;
};

const handleCloseEditDrawer = async () => {
  tableRef.value?.setCurrentRow();
  await closeEditDrawer();
  await applyQueryParameters();
};

const handleOpenEditDrawer = (visitorTimeline: VisitWithFields) => {
  const row = visitsWithFields.value?.find(
    (visit) => visit.id === visitorTimeline.id
  );
  tableRef.value?.setCurrentRow(row);
  selectedVisit.value = visitorTimeline;
  openEditDrawer(visitorTimeline);
};

watchEffect(() => {
  if (selectedId.value && editDrawerVisible.value) {
    const row = visitsWithFields.value?.find(
      (visit) => visit.id === selectedId.value
    );
    tableRef.value?.setCurrentRow(row);
  }
});

watch([sort, statusFilter, dateRangeFilter, categoryFilter], () => {
  handlePageChange(1);
});

const handleStatusChanged = (selectedOption: Status, pk?: string) => {
  if (!pk) return;

  switch (selectedOption) {
    case STATUS.OUT:
      signOutVisitor(pk);
      break;
    case STATUS.APPROVE:
      approveVisitor(pk);
      break;
    case STATUS.REJECT:
      rejectVisitor(pk);
      break;
  }
};

const handleActionSelected = async (type: string) => {
  switch (type) {
    case TimelineActionType.EXPORT:
      await handleExportVisits();
      break;
    case TimelineActionType.ANONYMIZE:
      anonymizeDialogVisible.value = true;
      break;
    case TimelineActionType.SIGN_OUT:
      signOutDialogVisible.value = true;
      break;
  }
};

const handleExportVisits = async () => {
  await exportVisits({
    ...actionExportFilters.value.export,
    ...(selectedRows.value.length > 0
      ? { ids: selectedRows.value.map((visit) => visit.visitId).join(",") }
      : {}),
  });
};

const handleSignOutVisits = async () => {
  signOutDialogVisible.value = false;

  const isSignOutAll = !signoutable.value.length;

  if (isSignOutAll) {
    const params = Object.keys(actionExportFilters.value.action).length
      ? { filter: actionExportFilters.value.action }
      : { all: true };
    await signOutAllVisits(params);
  } else {
    const visitIds = signoutable.value.reduce((arr, cur) => {
      if (cur?.visitId) {
        arr.push(`${cur.visitId}`);
      }
      return arr;
    }, [] as string[]);
    await signOutSelectedVisits(visitIds);
  }
};

const handleAnonymizeVisits = async () => {
  anonymizeDialogVisible.value = false;

  await anonymizeVisits(anonymizable.value.map((visit) => `${visit.visitId}`));
};

const actions = computed<TableAction[]>(() => {
  return [
    {
      name: selectedRows.value.length
        ? t("visitors.timeline.actions.export")
        : t("visitors.timeline.actions.exportAll"),
      count: exportActionCount.value,
      value: TimelineActionType.EXPORT,
      disabled:
        !exportActionCount.value || isExportLoading.value || isFetching.value,
    },
    {
      name: selectedRows.value.length
        ? t("visitors.timeline.actions.signOut")
        : t("visitors.timeline.actions.signOutAll"),
      count: signOutActionCount.value,
      value: TimelineActionType.SIGN_OUT,
      disabled:
        !signOutActionCount.value || isSignOutLoading.value || isFetching.value,
    },
    {
      name: t("visitors.timeline.actions.anonymize"),
      count: anonymizeActionCount.value,
      value: TimelineActionType.ANONYMIZE,
      disabled:
        !anonymizeActionCount.value ||
        isAnonymizeLoading.value ||
        isFetching.value ||
        !hasAdminRights.value,
    },
  ];
});

const applyQueryParameters = async () => {
  await setQueryParam("status", statusFilter.value, STATUS.ALL);
  await setQueryParam("category", categoryFilter.value, STATUS.ALL);
  await setQueryParam("search", searchFilter.value, "");
  await setDateQueryParam(dateRangeFilter.value);
};

const handleCloseAddDrawer = async () => {
  await closeAddDrawer();
  await applyQueryParameters();
};

const handleDateFilterChanged = (filter: DateRangeFilter) => {
  dateRangeFilter.value = filter;
  setDateQueryParam(filter);
};
</script>

<template>
  <div class="heading">
    <PageTitle :title="t('visitors.timeline.title')" />
    <div class="pills">
      <CounterPill
        :label="t('visitors.timeline.in')"
        :count="totalIn"
        colour="green"
      />
    </div>
  </div>
  <VisitorTimelineTable
    ref="tableRef"
    v-model:searchFilter="searchFilter"
    :has-filters="hasFilters"
    :table-data="visitsWithFields"
    :actions="actions"
    :is-loading="isLoading || (isFetching && isPreviousData)"
    :current-page="currentPage"
    :number-of-pages="numberOfPages"
    :default-sort-field="defaultSortField"
    :default-sort-order="defaultSortOrder"
    :date-range-filter="dateRangeFilter"
    @row-click="handleOpenEditDrawer"
    @page-change="handlePageChange"
    @sort-change="sort = $event"
    @selection-change="selectedRows = $event"
    @action-selected="handleActionSelected"
    @visitor-status-changed="handleStatusChanged"
    @add-visit="openAddDrawer"
    @update:search-filter="setQueryParam('search', $event, '')"
    @update:date-range-filter="handleDateFilterChanged"
  >
    <TableFilters :open="hasFilters">
      <VisitorTimelineFilters
        v-model:statusFilter="statusFilter"
        v-model:categoryFilter="categoryFilter"
        @update:status-filter="
          setQueryParam('status', $event ?? STATUS.ALL, STATUS.ALL)
        "
        @update:category-filter="
          setQueryParam('category', $event ?? STATUS.ALL, STATUS.ALL)
        "
      />
    </TableFilters>
  </VisitorTimelineTable>

  <AddVisitorTimelineDrawer
    :visible="addDrawerVisible"
    @close="handleCloseAddDrawer"
  />

  <EditVisitorTimelineDrawer
    :visible="editDrawerVisible"
    :visit-id="selectedId"
    :selected-visit="selectedVisit"
    @close="handleCloseEditDrawer"
    @status-changed="handleStatusChanged($event, selectedId)"
  >
    <DrawerHeaderIcons
      :prev-disabled="prevDisabled"
      :next-disabled="nextDisabled"
      @prev="goPrev"
      @next="goNext"
      @close="handleCloseEditDrawer"
    />
  </EditVisitorTimelineDrawer>

  <SignOutVisitDialog
    :total="signOutActionCount"
    :visible="signOutDialogVisible"
    :loading="isSignOutLoading"
    @close="signOutDialogVisible = false"
    @submit="handleSignOutVisits"
  />

  <AnonymizeVisitDialog
    :total="anonymizeActionCount"
    :visible="anonymizeDialogVisible"
    :loading="isAnonymizeLoading"
    @close="anonymizeDialogVisible = false"
    @submit="handleAnonymizeVisits"
  />
</template>

<style lang="scss" scoped>
.heading {
  display: flex;
  flex-direction: column;

  @media screen and (min-width: $breakpoint-md) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
}

.pills {
  display: flex;
  gap: 20px;

  @media screen and (max-width: $breakpoint-md) {
    margin-bottom: 10px;
  }
}
</style>
